import React from 'react';
import { TrashIcon } from './icons';

/**
 * The round buttons — one shape for the whole app.
 *
 * Silvia's instruction, and it is a design decision not a detail: "tutti i
 * pulsanti devono essere rotondi e verdi con il simbolo in bianco... tutte le
 * sezioni di tutta l'app devono avere la stessa grafica e struttura".
 *
 * Until now the same three actions wore three different costumes depending on
 * where you met them: a green pill saying ✓ here, a white outlined ✕ there, a
 * small grey square with a bin in it somewhere else. Somebody who is not
 * technical does not learn three costumes — they learn one, and every place
 * that departs from it is a place where they stop and read.
 *
 * So: a circle, the app's colour, a white symbol, 38px — the size of the back
 * arrow in the event form, which is where the shape came from.
 *
 * The bin is the deliberate exception and it is worth saying why. It has the
 * same circle and the same size, so the row is one family, but it keeps the
 * red: it is the only one of the three that cannot be undone, and a delete
 * that looks exactly like a confirm is a delete waiting to be tapped by
 * mistake. Same format, different colour — which is what "stessa dimensione e
 * formato" asks for and what safety needs.
 */

const SIZE = 38;

function base(size, disabled) {
  return {
    width: size, height: size, flexShrink: 0, padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%', border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition: 'opacity 0.15s, background 0.15s',
  };
}

/** ✓ — confirm. */
export function OkBtn({ onClick, label, disabled, size = SIZE, style }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      aria-label={label} title={label} className="tap-grow"
      style={{ '--tap-x': '4px', ...base(size, disabled), background: 'var(--color-primary)', color: '#fff', ...style }}>
      <svg viewBox="0 0 24 24" width={size * 0.53} height={size * 0.53} aria-hidden="true"
        stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13l4.5 4.5L19 7" />
      </svg>
    </button>
  );
}

/** ← — go back, leave, close without doing anything. */
export function BackBtn({ onClick, label, disabled, size = SIZE, style }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      aria-label={label} title={label} className="tap-grow"
      style={{ '--tap-x': '4px', ...base(size, disabled), background: 'var(--color-primary)', color: '#fff', ...style }}>
      <span className="rtl-flip" style={{ display: 'flex' }}>
        <svg viewBox="0 0 24 24" width={size * 0.53} height={size * 0.53} aria-hidden="true"
          stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
        </svg>
      </span>
    </button>
  );
}

/** + — make a new one. */
export function AddBtn({ onClick, label, disabled, size = SIZE, style }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      aria-label={label} title={label} className="tap-grow"
      style={{ '--tap-x': '4px', ...base(size, disabled), background: 'var(--color-primary)', color: '#fff', ...style }}>
      <svg viewBox="0 0 24 24" width={size * 0.55} height={size * 0.55} aria-hidden="true"
        stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  );
}

/** 🗑 — the one that cannot be undone. Same circle, same size, its own colour. */
export function DeleteBtn({ onClick, label, disabled, size = SIZE, style }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      aria-label={label} title={label} className="tap-grow"
      style={{ '--tap-x': '4px', ...base(size, disabled), background: '#c0392b', color: '#fff', ...style }}>
      <TrashIcon size={Math.round(size * 0.45)} />
    </button>
  );
}

/**
 * The row those buttons live in: confirm and back together on the leading
 * edge, the bin pushed to the other end. Always in this order, everywhere —
 * the distance between "yes" and "delete for ever" is the whole point.
 */
export function ActionRow({ children, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, ...style }}>
      {children}
    </div>
  );
}

export function Spacer() {
  return <div style={{ flex: 1 }} />;
}

/**
 * The little square — one drawing, wherever "all day" is shown or chosen.
 *
 * Silvia asked for two things that are really one: in the + you now TICK A BOX
 * to make an event last all day, and in Today the same box appears in front of
 * the title instead of the words "Tutto il giorno". So the thing you press and
 * the thing you later recognise are the same picture, which is the whole point
 * — the words had to be read, the box does not.
 *
 * `on` draws it filled with a white tick; off draws an empty outline.
 */
export function CheckSquare({ on = true, size = 20, style }) {
  const r = Math.max(3, Math.round(size * 0.22));
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false"
      style={{ flexShrink: 0, verticalAlign: '-0.18em', ...style }}>
      <rect x="2.5" y="2.5" width="19" height="19" rx={r}
        fill={on ? 'var(--color-primary)' : 'none'}
        stroke={on ? 'var(--color-primary)' : 'currentColor'}
        strokeWidth="2" />
      {on && (
        <path d="M7 12.4l3.4 3.4L17.2 9" fill="none" stroke="#fff"
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}
